import type { ProcedureStatus } from "@repo/domain";

const PROCEDURE_STATUSES = {
  inProgress: { fhir: "in-progress", legacy: "InProgress" },
  performed: { fhir: "completed", legacy: "Performed" },
  unableToComplete: { fhir: "stopped", legacy: "UnableToComplete" },
  didNotPerform: { fhir: "not-done", legacy: "DidNotPerform" },
  enteredInError: { fhir: "entered-in-error", legacy: "EnteredInError" },
} as const satisfies Record<ProcedureStatus, { fhir: string; legacy: string }>;

export function procedureStatusOf(status: string | undefined): ProcedureStatus | undefined {
  return (Object.keys(PROCEDURE_STATUSES) as ProcedureStatus[]).find((key) => {
    const entry = PROCEDURE_STATUSES[key];
    return status === key || status === entry.fhir || status === entry.legacy;
  });
}

export function procedureLegacyStatus(status: string | undefined) {
  const resolved = procedureStatusOf(status);
  return resolved ? PROCEDURE_STATUSES[resolved].legacy : undefined;
}

export function isProcedureCompleted(status: string | undefined): boolean {
  return procedureStatusOf(status) === "performed";
}

export function isProcedureResolved(status: string | undefined): boolean {
  const resolved = procedureStatusOf(status);
  return resolved === "performed" || resolved === "unableToComplete" || resolved === "didNotPerform";
}

export function isProcedureNotPerformed(status: string | undefined): boolean {
  return procedureStatusOf(status) === "didNotPerform";
}

export function canChartProcedure(orderStatus: string): boolean {
  return orderStatus !== "revoked";
}

export function isChartingOpen(status: string | undefined): boolean {
  return status === undefined || status === "notStarted" || procedureStatusOf(status) === "inProgress";
}

export function orderProcedureStatus(order: {
  treatmentProcedure?: { status: string };
  diagnosticProcedure?: { status: string };
}): ProcedureStatus | undefined {
  return procedureStatusOf(order.treatmentProcedure?.status ?? order.diagnosticProcedure?.status);
}

export function isWithdrawnProcedureOrder(order: {
  status: string;
  treatmentProcedure?: { status: string };
  diagnosticProcedure?: { status: string };
}): boolean {
  return order.status === "revoked" && !orderProcedureStatus(order);
}

export function hasProcedureCharting(procedure: {
  trials: readonly { lifecycle: { status: string } }[];
  observations: readonly { lifecycle?: { status: string }; status?: string }[];
  uncaptured: readonly string[];
  note?: string;
  notes?: string;
}): boolean {
  return (
    !!procedure.note?.trim() ||
    !!procedure.notes?.trim() ||
    procedure.trials.some((trial) => trial.lifecycle.status !== "EnteredInError") ||
    procedure.observations.some(
      (observation) => observation.lifecycle?.status !== "EnteredInError" && observation.status !== "entered-in-error",
    ) ||
    procedure.uncaptured.length > 0
  );
}

export const CHARTING_STATUSES = {
  notStarted: { label: "Not charted", tone: "idle", icon: "idle", timeline: "scheduled" },
  inProgress: { label: "Charting", tone: "open", icon: "active", timeline: "inProgress" },
  completed: { label: "Completed", tone: "done", icon: "done", timeline: "delivered" },
  amended: { label: "Amended", tone: "done", icon: "revised", timeline: "delivered" },
  underAmendment: { label: "Under amendment", tone: "open", icon: "revising", timeline: "inProgress" },
  didNotPerform: { label: "Did not perform", tone: "attention", icon: "skipped", timeline: "notPerformed" },
  unableToComplete: { label: "Unable to complete", tone: "attention", icon: "partial", timeline: "unableToComplete" },
  revoked: { label: "Revoked", tone: "idle", icon: "withdrawn", timeline: "unscheduled" },
  stopped: { label: "Stopped", tone: "attention", icon: "stopped", timeline: "unableToComplete" },
  enteredInError: { label: "Entered in error", tone: "error", icon: "error", timeline: "unscheduled" },
  withdrawn: { label: "Withdrawn", tone: "idle", icon: "withdrawn", timeline: "unscheduled" },
} as const;

export type ChartingStatus = keyof typeof CHARTING_STATUSES;
export type ChartedStatus = ChartingStatus | "performed";

export function chartingStatusOf(charted: ChartedStatus | undefined, orderStatus?: string): ChartingStatus {
  if (charted === "performed") return "completed";
  if (charted) return charted;
  if (orderStatus === "revoked") return "withdrawn";
  if (orderStatus === "completed") return "completed";
  return "notStarted";
}

export function chartingStatusLabel(status: ChartingStatus = "notStarted"): string {
  return CHARTING_STATUSES[status].label;
}

export function isSchedulingResolved(status: string): boolean {
  return isProcedureResolved(
    status === "delivered" ? "performed" : status === "notPerformed" ? "didNotPerform" : status,
  );
}
